import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { StorageApi, configApiRef, storageApiRef, useApi } from '@backstage/core-plugin-api';
import { CATALOG_FILTER_EXISTS, catalogApiRef } from '@backstage/plugin-catalog-react';
import { Chip, List, ListItem, ListItemIcon, ListItemText, Theme, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import ExtensionOutlinedIcon from '@material-ui/icons/ExtensionOutlined';
import { ReactNode, useMemo } from 'react';
import { readTips } from './homeConfig';
import { formatDate, useAsyncValue } from './homeUtils';

/** Optional ISO timestamp annotation teams can set to date a template or doc. */
export const CREATED_AT_ANNOTATION = 'backstage.io/createdAt';
const TECHDOCS_REF_ANNOTATION = 'backstage.io/techdocs-ref';

const STORAGE_BUCKET = 'devhub-home';
const FIRST_SEEN_KEY = 'discoverFirstSeen';
const NEW_FOR_DAYS = 30;
const DAY = 24 * 3600 * 1000;

const ENTITY_FIELDS = [
  'kind',
  'metadata.namespace',
  'metadata.name',
  'metadata.title',
  'metadata.description',
  'metadata.annotations',
  'spec.type',
];

interface DiscoverItem {
  ref: string;
  title: string;
  description?: string;
  link: string;
  /** Epoch millis; 0 when unknown. */
  date: number;
}

interface DiscoverData {
  templates: DiscoverItem[];
  docs: DiscoverItem[];
}

/**
 * The catalog does not expose creation timestamps, so items are dated by the
 * optional `backstage.io/createdAt` annotation, falling back to when this
 * browser first saw them. The very first visit records a zero baseline so the
 * existing catalog is not all flagged as new.
 */
function updateFirstSeen(storageApi: StorageApi, refs: string[]): Record<string, number> {
  const bucket = storageApi.forBucket(STORAGE_BUCKET);
  const previous = bucket.snapshot<Record<string, number>>(FIRST_SEEN_KEY).value;
  const now = Date.now();
  const next: Record<string, number> = {};
  for (const ref of refs) {
    next[ref] = previous ? previous[ref] ?? now : 0;
  }
  bucket.set(FIRST_SEEN_KEY, next).catch(() => undefined);
  return next;
}

function toItem(entity: Entity, link: string, firstSeen: Record<string, number>): DiscoverItem {
  const ref = stringifyEntityRef(entity);
  const annotated = Date.parse(entity.metadata.annotations?.[CREATED_AT_ANNOTATION] ?? '');
  return {
    ref,
    title: entity.metadata.title || entity.metadata.name,
    description: entity.metadata.description,
    link,
    date: Number.isNaN(annotated) ? firstSeen[ref] ?? 0 : annotated,
  };
}

const newestFirst = (a: DiscoverItem, b: DiscoverItem) => b.date - a.date || a.title.localeCompare(b.title);

/** Picks a tip that changes once a week (weeks start on Monday, UTC). */
export function tipOfTheWeekIndex(count: number, now: number = Date.now()): number {
  if (count <= 0) return -1;
  return Math.floor((now / DAY + 3) / 7) % count;
}

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  item: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  icon: {
    minWidth: 36,
    color: theme.palette.primary.main,
  },
  itemTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
  },
  newChip: {
    height: 20,
    fontSize: '0.68rem',
    fontWeight: 700,
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
    backgroundColor: 'transparent',
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  tip: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.type === 'dark' ? 0.16 : 0.06),
  },
  tipTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  tipIcon: {
    color: theme.palette.warning.main,
  },
  tipBody: {
    margin: 0,
  },
  // A blank line between the tip and its link: one line of body2 text.
  tipLink: {
    margin: 0,
    marginTop: '1.43em',
  },
}));

const DiscoverSection = ({
  title,
  link,
  linkTitle,
  children,
}: {
  title: string;
  link?: string;
  linkTitle?: string;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.section}>
      <Typography component="h3" className={classes.sectionTitle}>
        <span>{title}</span>
        {link && linkTitle && (
          <Link to={link} variant="caption">
            {linkTitle}
          </Link>
        )}
      </Typography>
      {children}
    </div>
  );
};

const DiscoverList = ({ items, icon, empty }: { items: DiscoverItem[]; icon: ReactNode; empty: string }) => {
  const classes = useStyles();
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {empty}
      </Typography>
    );
  }
  const now = Date.now();
  return (
    <List dense disablePadding>
      {items.map(item => (
        <ListItem key={item.ref} className={classes.item}>
          <ListItemIcon className={classes.icon}>{icon}</ListItemIcon>
          <ListItemText
            disableTypography
            primary={
              <Typography variant="body2" component="div" className={classes.itemTitle}>
                <Link to={item.link}>{item.title}</Link>
                {item.date > 0 && now - item.date <= NEW_FOR_DAYS * DAY && (
                  <Chip size="small" label="New" className={classes.newChip} title={formatDate(new Date(item.date).toISOString())} />
                )}
              </Typography>
            }
            secondary={
              item.description ? (
                <Typography variant="caption" color="textSecondary" component="div" className={classes.description}>
                  {item.description}
                </Typography>
              ) : undefined
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

export interface DiscoverCardProps {
  /** Items per section. */
  max?: number;
}

/** "Discover" card: newest software templates, recently added docs and an AI tip of the week. */
export const DiscoverCard = ({ max = 3 }: DiscoverCardProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const storageApi = useApi(storageApiRef);
  const configApi = useApi(configApiRef);
  const tips = useMemo(() => readTips(configApi), [configApi]);
  const tip = tips[tipOfTheWeekIndex(tips.length)];

  const { value, loading, error } = useAsyncValue<DiscoverData>(async () => {
    const [templates, docs] = await Promise.all([
      catalogApi.getEntities({ filter: { kind: 'Template' }, fields: ENTITY_FIELDS }),
      catalogApi.getEntities({
        filter: { [`metadata.annotations.${TECHDOCS_REF_ANNOTATION}`]: CATALOG_FILTER_EXISTS },
        fields: ENTITY_FIELDS,
      }),
    ]);
    const firstSeen = updateFirstSeen(
      storageApi,
      [...templates.items, ...docs.items].map(entity => stringifyEntityRef(entity)),
    );
    const namespace = (entity: Entity) => entity.metadata.namespace ?? 'default';
    return {
      templates: templates.items
        .map(entity =>
          toItem(entity, `/create/templates/${namespace(entity)}/${entity.metadata.name}`, firstSeen),
        )
        .sort(newestFirst)
        .slice(0, max),
      docs: docs.items
        .map(entity =>
          toItem(
            entity,
            `/docs/${namespace(entity)}/${entity.kind.toLocaleLowerCase('en-US')}/${entity.metadata.name}`,
            firstSeen,
          ),
        )
        .sort(newestFirst)
        .slice(0, max),
    };
  }, [catalogApi, storageApi, max]);

  let catalogContent: ReactNode;
  if (loading && !value) {
    catalogContent = <Progress />;
  } else if (error || !value) {
    catalogContent = (
      <Typography variant="body2" color="textSecondary">
        Templates and docs are unavailable right now.
      </Typography>
    );
  } else {
    catalogContent = (
      <>
        <DiscoverSection title="Latest templates" link="/create" linkTitle="All templates">
          <DiscoverList items={value.templates} icon={<ExtensionOutlinedIcon />} empty="No templates yet." />
        </DiscoverSection>
        <DiscoverSection title="Recently added docs" link="/docs" linkTitle="All docs">
          <DiscoverList items={value.docs} icon={<DescriptionOutlinedIcon />} empty="No documentation yet." />
        </DiscoverSection>
      </>
    );
  }

  return (
    <InfoCard title="Discover" variant="fullHeight">
      {catalogContent}
      {tip && (
        <DiscoverSection title="AI tip of the week">
          <div className={classes.tip}>
            <Typography variant="subtitle2" component="div" className={classes.tipTitle}>
              <EmojiObjectsOutlinedIcon fontSize="small" className={classes.tipIcon} />
              {tip.title}
            </Typography>
            <Typography variant="body2" component="p" className={classes.tipBody}>
              {tip.body}
            </Typography>
            {tip.link && (
              <Typography variant="body2" component="p" className={classes.tipLink}>
                <Link to={tip.link}>{tip.linkTitle || 'Learn more'}</Link>
              </Typography>
            )}
          </div>
        </DiscoverSection>
      )}
    </InfoCard>
  );
};
